import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";
import { Upload, DatePicker } from "antd";
import UTreeSelect from "components/UpStyled/UTreeSelect";

import api from "services/api";
import moment from "moment";

import DropZone from "components/UpStyled/DropZone";
import { seasonOptions } from "utils/data";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const STYLE_DESC_MAX_LENGTH = 2000;
const MAX_STYLE_NUMBER_COUNT = 12;
const MAX_TAG_LENGTH = 40;

/**
 * require more informations when user create a new style
 */
const FullCreateStyleModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      isDuplicate = false,
      style = {},
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
      collectionId = null,
      initialData = {},
    },
    ref
  ) => {
    const [sketchesFiles, setSketchesFiles] = useState([]);
    const [techPacksFiles, setTechPacksFiles] = useState([]);
    const [imageFiles, setImageFiles] = useState([]);
    const [styleDesc, setStyleDesc] = useState("");
    const [styleTags, setStyleTags] = useState([]);
    const [inputTag, setInputTag] = useState("");
    const [collectionOptions, setCollectionOptions] = useState([]);
    const [collectionsInfo, setCollectionsInfo] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [detailImage, setDetailImage] = useState(null);
    const [colorOptions, setColorOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [selectedGender, setSelectedGender] = useState(undefined);
    const [categoryOptions, setCategoryOptions] = useState([]);

    const {
      tree,
      actions: { findNodeByID, findParents },
    } = useContext(CategoryContext);

    if (ref) {
      useImperativeHandle(
        ref,
        () => ({
          sketchesFiles,
          techPacksFiles,
          imageFiles,
          styleTags,
          inputTag,
          setSketchesFiles,
          setTechPacksFiles,
          setImageFiles,
          setStyleTags,
        }),
        [sketchesFiles, techPacksFiles, imageFiles, styleTags, inputTag]
      );
    }

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        if (name == "sizeChart" && type == "change") {
          if (value.sizeChart?.meta?.sizes) {
            setValue("sizeRange", value.sizeChart?.meta?.sizes);
          }
        }
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
      if (isEdit || isDuplicate) {
        const styleMeta = style?.style ? style.style : {};
        let categoryValue = style?.type || {}; //{id:xxx,value:xxx} or id

        const categoryNode = findNodeByID(tree, categoryValue?.id);
        const node = findParents(tree, categoryValue?.id, { findKey: "id", returnKey: "id" });
        if (node && node.length > 0) {
          let genderNode = findNodeByID(tree, node[0]);
          setSelectedGender(genderNode);
          setCategoryOptions(genderNode?.children || []);
        }

        setSketchesFiles(styleMeta?.sketchesFiles || []);
        setTechPacksFiles(styleMeta?.techPacksFiles || []);
        setImageFiles(styleMeta?.imageFiles || []);

        if (styleMeta?.imageFiles && styleMeta?.imageFiles.length > 0) {
          setDetailImage(styleMeta.imageFiles[0]);
        }

        let collectionOpt = null;
        if (style?.collection) {
          collectionOpt = { label: style?.collection?.title, value: style?.collection?.id };
        } else if (style?.collection_id) {
          collectionOpt = collectionOptions.find((x) => x.value == style?.collection_id);
        }

        //reset color_way values
        const colorIds = style?.style?.color_way?.map((x) => x.value) || [];
        const color_way = colorOptions.filter((x) => colorIds.includes(x.value));

        //reset sizeChart value.
        //sizeRange is fixed and duplicated from size chart, it don't need to reset here.
        let sizeChart = sizeOptions.filter((x) => x.value == style?.style?.sizeChart?.value);
        if (sizeChart && sizeChart.length > 0) {
          sizeChart = sizeChart[0];
        }
        let resetPayload = {
          style_name: (isDuplicate ? "Copy of " : "") + style?.title,
          style_number: (isDuplicate ? "Copy of " : "") + styleMeta?.style_number,
          category: categoryNode?.id ? categoryNode.id : undefined,
          quantity: styleMeta?.quantity,
          cost_per_unit: styleMeta?.cost_per_unit,
          color_way: color_way,
          sizeChart: sizeChart,
          sizeRange: styleMeta?.sizeRange,
          description: style?.description,
          brand: styleMeta?.brand,
          year: styleMeta?.year ? moment(styleMeta?.year) : null,
          season: styleMeta?.season,
        };
        if (isEdit) {
          resetPayload["collection"] = collectionOpt;
        }
        reset(resetPayload);
        setStyleDesc(style?.description);
      } else {
        reset(initialData);
        setSketchesFiles(initialData?.sketchesFiles || []);
        setTechPacksFiles(initialData?.techPacksFiles || []);
        setImageFiles(initialData?.imageFiles || []);
        if (initialData?.imageFiles && initialData?.imageFiles.length > 0) {
          setDetailImage(initialData.imageFiles[0]);
        }
      }
    }, [isEdit, isDuplicate, tree]);

    useEffect(() => {
      if (collectionId) {
        const collectionOpt = collectionOptions.find((x) => x.value == collectionId);
        const collectionDefaultInfo = collectionsInfo.find((x) => x.id == collectionId);
        reset({
          collection: collectionOpt,
          brand: collectionDefaultInfo?.metadata?.brand,
          year: collectionDefaultInfo?.metadata?.year
            ? moment(collectionDefaultInfo.metadata.year)
            : null,
          season: collectionDefaultInfo?.metadata?.season,
        });
      }
    }, [collectionOptions]);

    useEffect(() => {
      retrieveCollections();
      retrievePalette();
      retrieveSizeCharts();
    }, []);

    const handleCreateStyle = async (data) => {
      try {
        setUploading(true);
        const sketchesFilesUploaded = await handleUploadFiles(sketchesFiles);
        const techPacksFilesUploaded = await handleUploadFiles(techPacksFiles);
        const imageFilesUploaded = await handleUploadFiles(imageFiles);
        let payload = {
          title: data["style_name"],
          status: "design",
          description: data["description"],
          options: [],
          collection_id: data["collection"]?.value || null,
          style: {
            ...style.style,
            style_number: data["style_number"],
            quantity: data["quantity"],
            cost_per_unit: data["cost_per_unit"],
            color_way: data["color_way"], //colorway tags
            sizeChart: data["sizeChart"],
            sizeRange: data["sizeRange"],
            sketchesFiles: sketchesFilesUploaded,
            techPacksFiles: techPacksFilesUploaded,
            imageFiles: imageFilesUploaded,
            brand: data["brand"],
            year: data["year"] ? moment(data["year"]).format("YYYY") : null,
            season: data["season"],
          },
        };
        if (isDuplicate) {
          if (style?.ancestors) {
            payload["ancestors"] = [...style.ancestors, style.id];
          } else {
            payload["ancestors"] = [style.id];
          }
        }

        if (data["category"]) {
          const node = findParents(tree, data["category"], {
            findKey: "id",
            returnKey: "id",
          });
          payload["type"] = { id: data["category"] };
          payload["path"] = node.join("/");
        } else {
          payload["type"] = null;
          payload["path"] = null;
        }

        const newCreatedProduct = await api.products.create(payload);

        setUploading(false);
        toast.success(
          `${
            isDuplicate ? "Duplicate Style" : isEdit ? "Edit Style" : "Create new Style"
          } successfully!`
        );
        handleClose();

        handleAfterOk(newCreatedProduct);

        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(
          `${isDuplicate ? "Duplicate Style" : isEdit ? "Edit Style" : "Create new Style"} failed!`
        );
        setUploading(false);
      }
    };

    const retrieveCollections = async () => {
      try {
        const { data } = await api.collections.list({ order: "-updated_at" });

        const collections = data["collections"] ? data["collections"] : [];
        const options = collections.map((x) => ({ label: x.title, value: x.id }));
        setCollectionOptions(options);
        setCollectionsInfo(collections);
      } catch (err) {
        console.log(err);
      }
    };

    const retrievePalette = async () => {
      try {
        const { data } = await api.palette.list({ order: "-updated_at" });

        const colors = data["colors"] ? data["colors"] : [];
        const options = colors.map((x) => ({ label: x.name, value: x.id, hex: x.hex }));
        setColorOptions(options);
        if (isEdit || isDuplicate) {
          //reset color_way values
          const colorIds = style?.style?.color_way?.map((x) => x.value) || [];
          const color_way = options.filter((x) => colorIds.includes(x.value));
          setValue("color_way", color_way);
        }
      } catch (err) {
        console.log(err);
      }
    };

    const retrieveSizeCharts = async () => {
      try {
        const { data } = await api.sizeChart.list({ order: "-updated_at" });

        let ownSizeCharts = data["sizes"] ? data["sizes"] : [];

        const shared = await api.sizeChart.sharedList({
          order: "-updated_at",
        });

        let sharedSizeCharts = shared?.data?.sizes || [];
        sharedSizeCharts = sharedSizeCharts.map((x) => ({ ...x, name: "Shared:" + x.name }));

        const allSizeCharts = [...ownSizeCharts, ...sharedSizeCharts];

        const options = allSizeCharts.map((x) => ({ label: x.name, value: x.id, meta: x }));
        setSizeOptions(options);
        //reset sizeChart value.
        //sizeRange is fixed and duplicated from size chart, it don't need to reset here.
        const sizeChart = options.filter((x) => x.value == style?.style?.sizeChart?.value);
        if (sizeChart && sizeChart.length > 0) {
          setValue("sizeChart", sizeChart[0]);
        }
      } catch (err) {
        console.log(err);
      }
    };

    const handleCollectionChange = (e) => {
      if (e?.value) {
        let selectedCollection = collectionsInfo.find((x) => x.id == e.value);
        if (selectedCollection?.metadata?.brand) {
          setValue("brand", selectedCollection?.metadata?.brand);
        }
        if (selectedCollection?.metadata?.year) {
          setValue("year", moment(selectedCollection?.metadata?.year));
        }
        if (selectedCollection?.metadata?.season) {
          setValue("season", selectedCollection?.metadata?.season);
        }
      }
    };

    const handleCreateColor = async (label) => {
      label = label.replace(/\W/g, "");
      if (label) {
        try {
          let option = { label, value: label, hex: null };
          await api.palette.create({ name: label });
          setColorOptions((prev) => [...prev, option]);
          let oldValue = getValues("color_way") || [];
          setValue("color_way", [...oldValue, option]);
          toast.success(`${label} has been saved successfully!`);
        } catch (err) {
          console.log(err);
          toast.error(`Color could not be saved!`);
        }
      }
    };

    const handleUploadImages = (data) => {
      const file = data.file;
      const image = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      setImageFiles((prevState) => [...prevState, image]);
      setDetailImage(image);

      // return api.uploads.create([file])
    };

    const handleGenderChange = (e) => {
      setSelectedGender(e);
      setValue("category", undefined);
      setCategoryOptions(e?.children || []);
    };

    const handleCreateCollection = async (label) => {
      label = label.replace(/\W/g, "");
      if (label) {
        try {
          let payload = {
            title: label,
            description: "",
          };

          const { data } = await api.collections.create(payload);
          const option = {
            label: label,
            value: data?.collection?.id,
          };
          setCollectionOptions((prev) => [...prev, option]);
          setValue("collection", option);
          toast.success(`${label} has been saved successfully!`);
        } catch (err) {
          console.log(err);
          toast.error(`Collection could not be created!`);
        }
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
        fullWidth={true}
      >
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {isDuplicate ? "Duplicate Style" : isEdit ? "Edit Style" : "Create a Style"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleCreateStyle)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12} md={5}>
                  <SuiBox>
                    <SuiBox ml={0.5}>
                      <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                        Style Images
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox mb={1.5} ml={0.5} mt={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="regular"
                        color="text"
                      >
                        The first image will be your primary image
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox sx={{ maxWidth: 256, margin: "auto" }}>
                      {imageFiles && imageFiles.length > 0 ? (
                        <div style={{ position: "relative" }}>
                          <SuiButton
                            variant="contained"
                            iconOnly
                            circular
                            sx={{
                              position: "absolute",
                              top: "50%",
                              right: 0,
                              zIndex: 4,
                              minWidth: "2.5rem",
                              opacity: 0.6,
                              "&:hover": { opacity: 0.9 },
                            }}
                            onClick={() => {
                              const index = imageFiles.findIndex((x) =>
                                x.preview
                                  ? x.preview == detailImage.preview
                                  : x.url == detailImage.url
                              );
                              if (index > -1) {
                                if (index < imageFiles.length - 1) {
                                  setDetailImage(imageFiles[index + 1]);
                                }
                              }
                            }}
                          >
                            <ArrowForwardIosIcon />
                          </SuiButton>
                          <SuiButton
                            variant="contained"
                            iconOnly
                            circular
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: 0,
                              zIndex: 4,
                              minWidth: "2.5rem",
                              opacity: 0.6,
                              textAlign: "center",
                              margin: "auto",
                              "&:hover": { opacity: 0.9 },
                            }}
                            onClick={() => {
                              const index = imageFiles.findIndex((x) =>
                                x.preview
                                  ? x.preview == detailImage.preview
                                  : x.url == detailImage.url
                              );
                              if (index > -1) {
                                if (index > 0) {
                                  setDetailImage(imageFiles[index - 1]);
                                }
                              }
                            }}
                          >
                            <ArrowBackIosNewIcon />
                          </SuiButton>
                          <ImageCardWrapper
                            data={detailImage}
                            mode={"normal"}
                            width={256}
                            height={320}
                            borderRadius="xl"
                            pdfPagination={true}
                          />
                        </div>
                      ) : (
                        <Upload.Dragger
                          name="design-images"
                          showUploadList={false}
                          customRequest={handleUploadImages}
                          accept="image/*, application/pdf"
                          className="rounded-2xl h-full"
                        >
                          <SuiBox
                            sx={{
                              overflow: "hidden",
                              width: "100%",
                              backgroundImage: ({
                                functions: { rgba, linearGradient },
                                palette: { gradients },
                              }) =>
                                `${linearGradient(
                                  rgba(gradients.secondary.main, 0.2),
                                  rgba(gradients.secondary.state, 0.4)
                                )}`,
                              height: "320px",
                              margin: "-16px 0px",
                              boxShadow: ({ boxShadows: { md } }) => md,
                              objectFit: "contain",
                              objectPosition: "center",
                            }}
                            display="flex"
                            alignItems="center"
                            position="relative"
                            minHeight="320px"
                            borderRadius="xl"
                          />
                        </Upload.Dragger>
                      )}
                    </SuiBox>
                  </SuiBox>
                  <SuiBox mt={4}>
                    <DropZone
                      files={imageFiles}
                      setfiles={setImageFiles}
                      options={{
                        accept: { "image/*": [".jpeg", ".png"], "application/pdf": [".pdf"] },
                      }}
                      onFileClick={(file) => setDetailImage(file)}
                      handleOnChange={(files) => {
                        if (!detailImage) {
                          setDetailImage(files[0]);
                        }
                      }}
                    />
                  </SuiBox>
                </Grid>

                <Grid item xs={12} sm={12} md={7}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            required
                            textTransform="capitalize"
                          >
                            Style Name
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Style Name"
                          required
                          {...register("style_name", { required: "A styled name is required!" })}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                            required
                          >
                            Style Number
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput
                          placeholder="Style Number"
                          required
                          {...register("style_number", {
                            required: "A styled number is required!",
                          })}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Gender
                          </SuiTypography>
                        </SuiBox>

                        <SuiSelect
                          options={tree}
                          value={selectedGender}
                          placeholder="Gender"
                          onChange={handleGenderChange}
                          backspaceRemovesValue
                          isClearable={true}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Category
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          control={control}
                          name="category"
                          render={({ field: { onChange, onBlur, value, name, ref } }) => (
                            <UTreeSelect
                              tree={categoryOptions}
                              fieldNames={{ label: "label", value: "id", children: "children" }}
                              onChange={(e) => {
                                onChange(e);
                              }}
                              allowClear={true}
                              value={value}
                              placeholder="Category"
                              treeDefaultExpandAll={true}
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Collection
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="collection"
                          control={control}
                          render={({ field: { onChange, onBlur, value, name, ref } }) => (
                            <SuiSelect
                              onChange={(e) => {
                                onChange(e);
                                handleCollectionChange(e);
                              }}
                              isDisabled={collectionId ? true : false}
                              sx={{ ":disabled": { backgroundColor: "info" } }}
                              value={value}
                              name={name}
                              ref={ref}
                              options={collectionOptions}
                              backspaceRemovesValue
                              isClearable
                              placeholder="Collection"
                              isCreatable
                              onCreateOption={handleCreateCollection}
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Brand
                          </SuiTypography>
                        </SuiBox>
                        <SuiInput placeholder="Brand" {...register("brand")} />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Year
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="year"
                          control={control}
                          render={({ field }) => (
                            <DatePicker
                              {...field}
                              picker="year"
                              placeholder="Year"
                              style={{
                                height: 40,
                                width: "100%",
                                borderRadius: "8px",
                                border: "1px solid #d2d6da",
                              }}
                              popupStyle={{
                                maxHeight: 400,

                                overflow: "auto",
                                zIndex: 9999,
                              }}
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Season
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="season"
                          control={control}
                          render={({ field }) => (
                            <SuiSelect
                              {...field}
                              options={seasonOptions}
                              placeholder="Season"
                              backspaceRemovesValue
                              isClearable
                              isCreatable
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                            info={`Type your color name to search or create.`}
                          >
                            Colorways
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="color_way"
                          control={control}
                          render={({ field }) => (
                            <SuiSelect
                              {...field}
                              options={colorOptions}
                              placeholder="Type your color name to sear or create"
                              backspaceRemovesValue
                              isClearable
                              isCreatable
                              isMulti
                              onCreateOption={handleCreateColor}
                            />
                          )}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Size Chart
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="sizeChart"
                          control={control}
                          render={({ field }) => {
                            return (
                              <SuiBox>
                                <SuiSelect
                                  {...field}
                                  placeholder="Size Chart"
                                  isSearchable={true}
                                  options={sizeOptions}
                                  isClearable
                                  escapeClearsValue
                                />{" "}
                              </SuiBox>
                            );
                          }}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Size Range
                          </SuiTypography>
                        </SuiBox>
                        <Controller
                          name="sizeRange"
                          control={control}
                          render={({ field }) => {
                            return (
                              <SuiBox>
                                <SuiInput {...field} placeholder="Size Range" />{" "}
                              </SuiBox>
                            );
                          }}
                        />
                      </SuiBox>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <SuiBox
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        height="100%"
                      >
                        <SuiBox mb={1} ml={0.5} mt={2} lineHeight={0} display="inline-block">
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            Description
                          </SuiTypography>
                        </SuiBox>

                        <SuiInput
                          multiline
                          rows="4"
                          componentsProps={{ input: { maxlength: STYLE_DESC_MAX_LENGTH } }}
                          {...register("description", {
                            maxLength: {
                              value: STYLE_DESC_MAX_LENGTH,
                              message: `Maxmium length should not exceed ${STYLE_DESC_MAX_LENGTH} characters`,
                            },
                            value: styleDesc,
                            onChange: (e) => {
                              setStyleDesc(e.target.value);
                            },
                          })}
                          placeholder="Your style description..."
                        />

                        <SuiBox display="flex" justifyContent="space-between">
                          <div>
                            <ErrorMessage
                              errors={errors}
                              name="description"
                              render={({ message }) => (
                                <SuiTypography
                                  variant="text"
                                  fontWeight="regular"
                                  fontSize="12px"
                                  color="error"
                                  sx={{ ml: 1 }}
                                >
                                  {message}
                                </SuiTypography>
                              )}
                            />
                          </div>
                          <SuiTypography
                            variant="text"
                            color="secondary"
                            fontWeight="regular"
                            fontSize="14px"
                          >
                            {styleDesc ? styleDesc.length : 0}/{STYLE_DESC_MAX_LENGTH}
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      {isDuplicate ? "Duplicate" : isEdit ? "Save" : "Create"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default FullCreateStyleModal;
